import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Meeting Slice - Manages meeting/room state including E2EE, recording, etc.
 */
export interface MeetingDetailsState {
  roomId: string;
  roomName: string | null;

  e2eeEnabled: boolean;
  e2eePassphrase: string | null;

  codec: string | null;
  hqEnabled: boolean;

  // Meeting status
  isConnected: boolean;
  isConnecting: boolean;

  // Recording status
  isRecording: boolean;
  isRecordingLoading: boolean;
  recordingError: string | null;
}

// State Interface
interface MeetingState {
  meetingDetails: MeetingDetailsState | null;
}

// Initial Meeting Details (default values)
const defaultMeetingDetails: MeetingDetailsState = {
  roomId: "",
  roomName: null,
  e2eeEnabled: false,
  e2eePassphrase: null,
  codec: null,
  hqEnabled: false,
  isConnected: false,
  isConnecting: false,
  isRecording: false,
  isRecordingLoading: false,
  recordingError: null
};

// Initial State
const initialState: MeetingState = {
  meetingDetails: null
};

// Slice Definition
const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    /** Sets the complete meeting details. */
    setMeetingDetailsRedux(state, action: PayloadAction<Partial<MeetingDetailsState>>) {
      state.meetingDetails = {
        ...defaultMeetingDetails,
        ...action.payload
      };
    },

    /** Updates meeting details partially (real-time). */
    updateMeetingDetailsRedux(state, action: PayloadAction<Partial<MeetingDetailsState>>) {
      if (state.meetingDetails) {
        state.meetingDetails = {
          ...state.meetingDetails,
          ...action.payload
        };
      }
    },

    /** Clears all meeting details (on leave). */
    clearMeetingDetailsRedux(state) {
      state.meetingDetails = null;
    }
  }
});

export const { setMeetingDetailsRedux, updateMeetingDetailsRedux, clearMeetingDetailsRedux } = meetingSlice.actions;

// Get complete meeting details
export const selectMeetingDetails = (state: { meeting: MeetingState }) => state.meeting.meetingDetails;

// Get specific meeting details
export const selectE2EEEnabled = (state: { meeting: MeetingState }) =>
  state.meeting.meetingDetails?.e2eeEnabled ?? false;

// Get recording status
export const selectIsRecording = (state: { meeting: MeetingState }) =>
  state.meeting.meetingDetails?.isRecording ?? false;

// Get room name
export const selectRoomName = (state: { meeting: MeetingState }) => state.meeting.meetingDetails?.roomName ?? null;

export default meetingSlice.reducer;
